import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, BellRing, CalendarClock, ChevronRight, FileText, Info, Lock, PiggyBank, Refrigerator, ShoppingBag, Target, TrendingDown, Wallet, Wrench, Landmark } from "lucide-react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { C, ChartTooltip, anim, axis } from "@/components/common/ChartKit";
import { CountUp } from "@/components/common/CountUp";
import { EmptyState } from "@/components/common/EmptyState";
import { GoalsWidget } from "@/components/dashboard/GoalsWidget";
import { TopExpenses } from "@/components/dashboard/TopExpenses";
import { SpendingYearDialog } from "@/components/dashboard/SpendingYear";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { CATEGORY_LABELS } from "@/hooks/use-assets";
import { useFormat } from "@/lib/format";
import { cn } from "@/lib/utils";

export const WidgetCard = ({ id, title, icon: Icon, className = "", dark = false, tone = "mint", children, delay = 0 }) => (
  <section
    className={cn(dark ? "hero-navy rounded-2xl shadow-xl" : "card-surface tone-wash lift", `tone-${tone}`, "fade-up flex flex-col p-5 sm:p-6", className)}
    style={{ animationDelay: `${delay}ms` }}
    data-testid={`widget-${id}`}
  >
    <div className="mb-4 flex items-center gap-2.5">
      <span className={cn("flex h-8 w-8 items-center justify-center rounded-xl", dark ? "bg-mint/20 text-mint" : "tone-chip")}>
        <Icon className="h-4 w-4" strokeWidth={2.4} />
      </span>
      <h2 className={cn("text-sm font-extrabold uppercase tracking-[0.1em]", dark ? "text-mint" : "text-foreground")}>{title}</h2>
    </div>
    {children}
  </section>
);

const FinanceLocked = ({ reason }) => (
  <EmptyState compact icon={Lock} testid="finance-locked"
    title={reason === "role" ? "Finances personnelles privées" : "Aucune donnée financière"}
    text={reason === "role" ? "Votre rôle dans ce foyer ne donne pas accès à un espace financier." : "Ajoutez vos comptes ou saisissez vos budgets (module Finance — Partie 2)."} />
);

const NoData = ({ text }) => <EmptyState compact title="Rien pour l'instant" text={text} />;

const Budget = ({ data }) => {
  const { money } = useFormat();
  const f = data.finance;
  if (!f) return <div className="[&_div]:text-[#F8FAF8] [&_p]:!text-[#F8FAF8]/80"><FinanceLocked reason={data.finance_reason} /></div>;
  const pct = Math.min(100, Math.round((f.spent_this_month / f.monthly_income) * 100));
  return (
    <div className="flex flex-1 flex-col justify-between gap-6">
      <div>
        <div className="num text-4xl font-extrabold tracking-tight sm:text-5xl" data-testid="budget-available-value"><CountUp value={f.available_budget} format={money} /></div>
        <p className="mt-1 text-sm text-[#F8FAF8]/70">disponible jusqu'à la fin du mois</p>
      </div>
      <div>
        <div className="h-2 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-mint transition-all duration-700" style={{ width: `${pct}%` }} /></div>
        <div className="mt-2 flex justify-between text-xs text-[#F8FAF8]/70">
          <span>Dépensé {money(f.spent_this_month)}</span><span>Revenus {money(f.monthly_income)}</span>
        </div>
      </div>
    </div>
  );
};

const Spending = ({ data }) => {
  const { money } = useFormat();
  const [year, setYear] = useState(false);
  const f = data.finance;
  const { data: h } = useQuery({ queryKey: ["planning", "history", new Date().getFullYear()], queryFn: () => api.get("/planning/personal/me/history").then((r) => r.data), enabled: !!f });
  if (!f) return <FinanceLocked reason={data.finance_reason} />;
  const past = (h?.rows || []).filter((r) => r.amount != null);
  const series = past.length ? past.slice(-6).map((r) => ({ month: new Date(`${r.month}-01T12:00:00`).toLocaleDateString("fr-FR", { month: "short" }), amount: r.amount })) : f.monthly_spending;
  const current = past.length ? past[past.length - 1].amount : f.spent_this_month;
  return (
    <div>
      <div className="flex items-start justify-between gap-2">
        <div><div className="num text-3xl font-extrabold" data-testid="spending-month-value"><CountUp value={current} format={money} /></div>
          <p className="text-xs text-muted-foreground">ce mois-ci</p></div>
        <button onClick={() => setYear(true)} className="shrink-0 rounded-full border border-border bg-card px-3 py-1 text-[11px] font-semibold hover:bg-secondary" data-testid="spending-year-open">Voir toute l'année</button>
      </div>
      <SpendingYearDialog open={year} onOpenChange={setYear} />
      <div className="mt-4 h-36 cursor-pointer" onClick={() => setYear(true)} data-testid="spending-chart">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={series} margin={{ top: 8, right: 6, left: 6, bottom: 0 }}>
            <defs>
              <linearGradient id="spendFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C.mint} stopOpacity={0.45} /><stop offset="100%" stopColor={C.mint} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="month" {...axis} />
            <Tooltip cursor={{ stroke: C.mint, strokeDasharray: "4 4", strokeOpacity: 0.6 }} content={<ChartTooltip names={{ amount: "Dépenses" }} />} />
            <Area type="monotone" dataKey="amount" stroke={C.mint} strokeWidth={3} fill="url(#spendFill)" dot={{ r: 3, fill: C.mint, strokeWidth: 0 }} activeDot={{ r: 6, strokeWidth: 3, stroke: "#fff" }} {...anim} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <TopExpenses />
    </div>
  );
};

const Stat = ({ label, value, tone = "mint" }) => (
  <div className={cn("tone-tile rounded-xl p-3", `tone-${tone}`)}>
    <div className="num text-xl font-bold">{value}</div>
    <div className="text-[11px] text-foreground/70">{label}</div>
  </div>
);

const PATRI_COLORS = ["#87B8A5", "#D8BD78", "#9DB4D8", "#C9A6D8", "#E3A79A", "#A8C8A0", "#B8B0A0"];

const Patrimoine = ({ data }) => {
  const { money } = useFormat();
  if (!data.assets) return <NoData text="Inventoriez vos biens pour estimer votre patrimoine." />;
  const a = data.assets;
  const cats = a.by_category || [];
  return (
    <div>
      <div className="num text-3xl font-extrabold" data-testid="patrimoine-value"><CountUp value={a.estimated_value} format={money} /></div>
      <p className="mt-1 text-xs text-muted-foreground">valeur actuelle estimée de {a.equipment_count} {a.equipment_count > 1 ? "biens" : "bien"}{a.purchase_value ? ` · achetés ${money(a.purchase_value)}` : ""}</p>
      {cats.length > 0 && (
        <>
          <div className="mt-4 flex h-2.5 overflow-hidden rounded-full bg-secondary">
            {cats.map((c, i) => <div key={c.category} style={{ width: `${a.estimated_value ? (c.value / a.estimated_value) * 100 : 0}%`, background: PATRI_COLORS[i % PATRI_COLORS.length] }} />)}
          </div>
          <ul className="mt-3 space-y-1.5" data-testid="patrimoine-categories">
            {cats.map((c, i) => (
              <li key={c.category} className="flex items-center gap-2 text-sm" data-testid={`patrimoine-cat-${c.category}`}>
                <i className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: PATRI_COLORS[i % PATRI_COLORS.length] }} />
                <span className="min-w-0 flex-1 truncate">{CATEGORY_LABELS[c.category] || c.category} <span className="text-[11px] text-muted-foreground">· {c.count}</span></span>
                <span className="num shrink-0 font-semibold">{money(c.value)}</span>
                <span className="num w-9 shrink-0 text-right text-[11px] text-muted-foreground">{a.estimated_value ? Math.round((c.value / a.estimated_value) * 100) : 0} %</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

const Equipments = ({ data }) => {
  if (!data.assets) return <NoData text="Vos équipements et garanties apparaîtront ici." />;
  const a = data.assets;
  return (
    <div className="grid grid-cols-3 gap-2">
      <Stat label="Équipements" value={a.equipment_count} tone="sky" />
      <Stat label="Garanties" value={a.warranties_active} tone="mint" />
      <Stat label="Expirent" value={a.warranties_expiring} tone="peach" />
    </div>
  );
};

const ListRows = ({ rows, empty }) => {
  if (!rows?.length) return <NoData text={empty} />;
  return (
    <ul className="divide-y divide-border">
      {rows.map((r) => (
        <li key={r.key} className="flex items-center justify-between gap-3 py-2.5 text-sm">
          <span className="min-w-0 truncate">{r.label}</span>
          <span className="shrink-0 text-xs text-muted-foreground">{r.meta}</span>
        </li>
      ))}
    </ul>
  );
};

const Documents = ({ data }) => {
  const { date } = useFormat();
  return <ListRows empty="Vos factures et contrats récents s'afficheront ici." rows={data.documents.map((d) => ({ key: d.name, label: d.name, meta: `${d.type} · ${date(d.date)}` }))} />;
};
const Maintenance = ({ data }) => {
  const { date } = useFormat();
  return <ListRows empty="Aucun entretien planifié." rows={data.maintenance.map((m) => ({ key: m.label, label: m.label, meta: date(m.due) }))} />;
};
const Tickets = ({ data }) => (
  <ListRows empty="Aucune demande de réparation en cours." rows={data.tickets.map((t) => ({ key: t.ref, label: t.label, meta: t.status }))} />
);
const Market = ({ data }) => {
  if (!data.market) return <NoData text="Vendez vos biens inutilisés depuis votre inventaire (Partie 5)." />;
  const m = data.market;
  return (
    <div className="grid grid-cols-3 gap-2">
      <Stat label="Annonces" value={m.active_listings} tone="butter" /><Stat label="Vues" value={m.views} tone="sky" /><Stat label="Messages" value={m.messages} tone="rose" />
    </div>
  );
};

const LEVEL = { danger: [AlertTriangle, "bg-destructive text-white"], warning: [AlertTriangle, "bg-warning text-white"], info: [Info, "tone-chip tone-sky"] };
const LEVEL_BADGE = { danger: "badge-alert animate-alert-pulse", warning: "badge-decision", info: "rounded-full px-2.5 py-0.5 text-[11px] font-bold tone-chip tone-sky" };
const ROW = { danger: "alert-strong animate-alert-pulse font-semibold", warning: "decision-strong", info: "rounded-xl border border-border bg-card/80" };
const LEVEL_LABEL = { danger: "urgentes", warning: "à surveiller", info: "info" };
const VISIBLE = 4;
const Alerts = ({ data }) => {
  const [all, setAll] = useState(false);
  if (!data.alerts.length) return <NoData text="Aucune alerte. Tout est sous contrôle." />;
  const counts = data.alerts.reduce((c, a) => ({ ...c, [a.level]: (c[a.level] || 0) + 1 }), {});
  const list = all ? data.alerts : data.alerts.slice(0, VISIBLE);
  return (
    <div className="space-y-2" data-testid="alerts-list">
      <div className="flex flex-wrap gap-1.5" data-testid="alerts-summary">
        {["danger", "warning", "info"].filter((l) => counts[l]).map((l) => (
          <span key={l} className={LEVEL_BADGE[l]}>{counts[l]} {LEVEL_LABEL[l]}</span>
        ))}
      </div>
      <ul className="stagger space-y-1.5">
        {list.map((a) => {
          const [Icon, tone] = LEVEL[a.level] || LEVEL.info;
          const body = (<>
            <span className={cn("flex h-6 w-6 shrink-0 items-center justify-center rounded-lg", tone)}><Icon className="h-3.5 w-3.5" strokeWidth={2.5} /></span>
            <span className="min-w-0 flex-1 line-clamp-2">{a.label}</span>
            {a.link && <ChevronRight className="h-4 w-4 shrink-0 opacity-70" />}
          </>);
          const cls = cn("flex items-center gap-2.5 px-3 py-2.5 text-xs leading-snug sm:text-sm", ROW[a.level] || ROW.info);
          return a.link ? (
            <li key={a.label}><Link to={a.link} className={cn(cls, "lift")} data-testid="alert-item">{body}</Link></li>
          ) : (
            <li key={a.label} className={cls} data-testid="alert-item">{body}</li>
          );
        })}
      </ul>
      {data.alerts.length > VISIBLE && (
        <button onClick={() => setAll(!all)} className="text-xs font-semibold text-primary underline-offset-2 hover:underline" data-testid="alerts-toggle">
          {all ? "Réduire" : `Voir les ${data.alerts.length - VISIBLE} autres alertes`}
        </button>
      )}
    </div>
  );
};

export const WIDGETS = {
  alerts: { title: "Alertes importantes", icon: BellRing, Comp: Alerts, span: "md:col-span-2", tone: "rose" },
  budget: { title: "Budget disponible", icon: Wallet, Comp: Budget, span: "md:col-span-2", dark: true },
  spending: { title: "Dépenses mensuelles", icon: TrendingDown, Comp: Spending, span: "md:col-span-2", tone: "mint" },
  patrimoine: { title: "Valeur du patrimoine", icon: Landmark, Comp: Patrimoine, span: "", tone: "butter" },
  goals: { title: "Économies & objectifs", icon: Target, Comp: GoalsWidget, span: "", tone: "peach" },
  equipments: { title: "Équipements & garanties", icon: Refrigerator, Comp: Equipments, span: "md:col-span-2", tone: "sky" },
  documents: { title: "Documents récents", icon: FileText, Comp: Documents, span: "md:col-span-2", tone: "lilac" },
  maintenance: { title: "Entretiens à prévoir", icon: CalendarClock, Comp: Maintenance, span: "", tone: "butter" },
  tickets: { title: "Tickets SAV", icon: Wrench, Comp: Tickets, span: "", tone: "peach" },
  market: { title: "Activité Market", icon: ShoppingBag, Comp: Market, span: "md:col-span-2", tone: "rose" },
};

export const SavingsIcon = PiggyBank;
